// client/src/components/pacotesCliente/PacoteClienteForm.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutatePacotesCliente } from '../../hooks/usePacotesCliente';
import { useClientes } from '../../hooks/useClientes';
import { useOfertas } from '../../hooks/useOfertas';
import { useUnidadesParaFormulario } from '../../hooks/useUnidades';
import { useMetodosPagamento } from '../../hooks/useMetodosPagamento';
import { useMe } from '../../hooks/useAuth';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';
import useAlert from '../../hooks/useAlert';
import { XCircle, Loader2, ArrowRightLeft } from 'lucide-react';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_LABELS = ['Detalhes da Venda', 'Pagamento', 'Observações'];

const initialState = {
    clienteId: '',
    ofertaId: '',
    pacoteDePrecoId: '',
    unidadeId: '',
    metodoPagamentoId: '',
    observacoes: ''
};

const formatDateTime = (value) => value ? new Date(value).toLocaleString('pt-BR') : '—';

const SESSAO_STATUS_LABEL = {
    agendada: 'Agendada',
    confirmada: 'Confirmada',
    realizada: 'Realizada',
    cancelada: 'Cancelada'
};

const SESSAO_STATUS_CLASS = {
    agendada: 'badge-warning',
    confirmada: 'badge-warning',
    realizada: 'badge-success',
    cancelada: 'badge-danger'
};

const PacoteClienteForm = ({ onCloseForm, onSaveSuccess, current }) => {
    const { setAlert } = useAlert();
    const { accountId } = useMe();
    // ✅ ACHADO: formatCurrency era fixo em BRL, sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const formatCurrency = (value) => formatCurrencyUtil(value || 0, currency, locale);
    const { addPacote, updatePacote, transferirPacote } = useMutatePacotesCliente();

    const isEditing = !!current?._id;
    const loading = addPacote.isPending || updatePacote.isPending;

    const [formData, setFormData] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const [statusEdicao, setStatusEdicao] = useState('Ativo');
    const [observacoesEdicao, setObservacoesEdicao] = useState('');
    const [novoClienteId, setNovoClienteId] = useState('');
    const [motivoTransferencia, setMotivoTransferencia] = useState('');

    const { data: clientesData, isLoading: loadingClientes } = useClientes({ limit: 1000 }, accountId);
    const clientes = clientesData?.data || [];
    const clienteAtualId = current?.cliente?._id || current?.cliente;
    const clientesParaTransferencia = clientes.filter(c => (c._id || c.id) !== clienteAtualId);

    const { data: ofertasData, isLoading: loadingOfertas } = useOfertas({ account: accountId, limit: 500, ativo: true });
    const ofertasVendaveis = useMemo(() => {
        const ofertas = ofertasData?.data || [];
        return ofertas.filter(o =>
            o.tipoDePrecificacao === 'Por Pacote' ||
            (o.tipoDePrecificacao === 'Fixo' && o.tipo === 'Individual')
        );
    }, [ofertasData]);

    const { data: unidadesData, isLoading: loadingUnidades } = useUnidadesParaFormulario(accountId);
    const unidades = unidadesData?.data || [];

    const { data: metodosData } = useMetodosPagamento(accountId);
    const metodos = metodosData?.data || [];

    const ofertaSelecionada = useMemo(
        () => ofertasVendaveis.find(o => o._id === formData.ofertaId) || null,
        [ofertasVendaveis, formData.ofertaId]
    );

    const pacoteDePrecoSelecionado = useMemo(() => {
        if (!ofertaSelecionada || ofertaSelecionada.tipoDePrecificacao !== 'Por Pacote') return null;
        return (ofertaSelecionada.pacotesDePreco || []).find(p => p._id === formData.pacoteDePrecoId) || null;
    }, [ofertaSelecionada, formData.pacoteDePrecoId]);

    useEffect(() => {
        if (isEditing && current) {
            setStatusEdicao(current.status || 'Ativo');
            setObservacoesEdicao(current.metadata?.observacoes || '');
            setNovoClienteId('');
            setMotivoTransferencia('');
        } else {
            setFormData(initialState);
            setCurrentStep(1);
        }
    }, [current, isEditing]);

    const handleTransferir = useCallback(() => {
        if (!novoClienteId) {
            setAlert('Selecione o cliente de destino.', 'warning');
            return;
        }
        if (!window.confirm('Tem certeza que deseja transferir este pacote para outro cliente? Esta ação fica registrada no histórico do pacote.')) {
            return;
        }
        transferirPacote.mutate(
            { id: current._id, novoClienteId, motivo: motivoTransferencia },
            {
                onSuccess: () => {
                    setNovoClienteId('');
                    setMotivoTransferencia('');
                    onSaveSuccess ? onSaveSuccess() : onCloseForm();
                }
            }
        );
    }, [novoClienteId, motivoTransferencia, current, transferirPacote, onSaveSuccess, onCloseForm, setAlert]);

    const onChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'ofertaId') next.pacoteDePrecoId = '';
            if (name === 'unidadeId' && !value) next.metodoPagamentoId = '';
            return next;
        });
    }, []);

    const onSubmit = (e) => {
        e.preventDefault();

        if (isEditing) {
            updatePacote.mutate(
                { id: current._id, pacoteData: { status: statusEdicao, observacoes: observacoesEdicao } },
                { onSuccess: () => (onSaveSuccess ? onSaveSuccess() : onCloseForm()) }
            );
            return;
        }

        if (!formData.clienteId) return setAlert('Selecione o cliente.', 'warning');
        if (!formData.ofertaId) return setAlert('Selecione a oferta.', 'warning');
        if (ofertaSelecionada?.tipoDePrecificacao === 'Por Pacote' && !formData.pacoteDePrecoId) {
            return setAlert('Selecione o pacote de preço da oferta.', 'warning');
        }

        const dataToSubmit = {
            clienteId: formData.clienteId,
            ofertaId: formData.ofertaId,
            pacoteDePrecoId: formData.pacoteDePrecoId || undefined,
            unidadeId: formData.unidadeId || undefined,
            metodoPagamentoId: formData.metodoPagamentoId || undefined,
            observacoes: formData.observacoes
        };

        addPacote.mutate(dataToSubmit, {
            onSuccess: () => (onSaveSuccess ? onSaveSuccess() : onCloseForm())
        });
    };

    if (isEditing) {
        return (
            <form onSubmit={onSubmit} noValidate>
                <div className="form-header">
                    <h3 className="form-title">Editar Pacote do Cliente</h3>
                    <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
                        <XCircle size={24} />
                    </button>
                </div>

                <fieldset className="form-section">
                    <legend className="form-section-title">Resumo</legend>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label>Cliente</label>
                            <p className="form-static-text">{current.cliente?.user?.name || 'Cliente não encontrado'}</p>
                        </div>
                        <div className="form-group">
                            <label>Oferta</label>
                            <p className="form-static-text">{current.oferta?.nome || 'Oferta não encontrada'}</p>
                        </div>
                        <div className="form-group">
                            <label>Pacote</label>
                            <p className="form-static-text">{current.pacoteComprado?.nome} ({current.pacoteComprado?.sessoes} sessões - {formatCurrency(current.pacoteComprado?.valor)})</p>
                        </div>
                        <div className="form-group">
                            <label>Validade</label>
                            <p className="form-static-text">{current.dataValidade ? new Date(current.dataValidade).toLocaleDateString('pt-BR') : '—'}</p>
                        </div>
                    </div>
                </fieldset>

                <fieldset className="form-section">
                    <legend className="form-section-title">Status e Observações</legend>
                    <div className="form-grid-container">
                        <div className="form-group">
                            <label htmlFor="statusEdicao">Status</label>
                            <select
                                id="statusEdicao"
                                value={statusEdicao}
                                onChange={(e) => setStatusEdicao(e.target.value)}
                                className="form-input"
                            >
                                <option value="Ativo">Ativo</option>
                                <option value="Suspenso">Suspenso</option>
                                <option value="Cancelado">Cancelado</option>
                            </select>
                            <p className="form-hint">Status "Concluído"/"Expirado" são definidos automaticamente pelo sistema.</p>
                        </div>
                        <div className="form-group full-width">
                            <label htmlFor="observacoesEdicao">Observações</label>
                            <textarea
                                id="observacoesEdicao"
                                value={observacoesEdicao}
                                onChange={(e) => setObservacoesEdicao(e.target.value)}
                                rows="3"
                                className="form-input"
                            />
                        </div>
                    </div>
                </fieldset>

                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Histórico de Sessões ({(current.sessoesUtilizadas || []).length})
                    </legend>
                    {(current.sessoesUtilizadas || []).length === 0 ? (
                        <p className="form-hint">Nenhuma sessão utilizada até o momento.</p>
                    ) : (
                        <div className="table-wrapper">
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Status</th>
                                        <th>Profissional</th>
                                        <th>Unidade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...(current.sessoesUtilizadas || [])]
                                        .sort((a, b) => new Date(b.dataUtilizacao) - new Date(a.dataUtilizacao))
                                        .map((sessao) => (
                                            <tr key={sessao._id}>
                                                <td className="table-cell">{formatDateTime(sessao.dataUtilizacao)}</td>
                                                <td className="table-cell">
                                                    <span className={`badge ${SESSAO_STATUS_CLASS[sessao.status] || 'badge-gray'}`}>
                                                        {SESSAO_STATUS_LABEL[sessao.status] || sessao.status}
                                                    </span>
                                                </td>
                                                <td className="table-cell">{sessao.profissional?.name || '—'}</td>
                                                <td className="table-cell">{sessao.unidade?.nome || '—'}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </fieldset>

                {current.status === 'Ativo' && (
                    <fieldset className="form-section">
                        <legend className="form-section-title">Transferir Pacote para Outro Cliente</legend>
                        <p className="form-hint">Só é possível transferir pacotes com status "Ativo". A transferência fica registrada no histórico do pacote.</p>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor="novoClienteId">Novo Cliente</label>
                                <select
                                    id="novoClienteId"
                                    value={novoClienteId}
                                    onChange={(e) => setNovoClienteId(e.target.value)}
                                    className="form-input"
                                    disabled={loadingClientes}
                                >
                                    <option value="">-- Selecione o cliente de destino --</option>
                                    {clientesParaTransferencia.filter(c => c?._id || c?.id).map(c => (
                                        <option key={c._id || c.id} value={c._id || c.id}>{c.nomeCompleto || c.user?.name || 'Cliente sem nome'}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="motivoTransferencia">Motivo (opcional)</label>
                                <input
                                    id="motivoTransferencia"
                                    type="text"
                                    value={motivoTransferencia}
                                    onChange={(e) => setMotivoTransferencia(e.target.value)}
                                    className="form-input"
                                    placeholder="Ex.: Pacote comprado para familiar"
                                />
                            </div>
                        </div>
                        <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                            <button
                                type="button"
                                onClick={handleTransferir}
                                disabled={transferirPacote.isPending || !novoClienteId}
                                className="action-button action-button-secondary"
                            >
                                {transferirPacote.isPending ? <Loader2 className="animate-spin icon" /> : <ArrowRightLeft size={18} className="icon" />}
                                Transferir Pacote
                            </button>
                        </div>
                    </fieldset>
                )}

                <div className="form-actions">
                    <button type="button" onClick={onCloseForm} className="action-button action-button-secondary" disabled={loading}>
                        <XCircle size={18} className="icon" />Cancelar
                    </button>
                    <FormStepNavigation
                        currentStep={1}
                        totalSteps={1}
                        onBack={() => {}}
                        onNext={() => {}}
                        isSubmitting={loading}
                        submitLabel="Salvar Alterações"
                    />
                </div>
            </form>
        );
    }

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">Vender Novo Pacote a Cliente</h3>
                <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
                    <XCircle size={24} />
                </button>
            </div>

            <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

            {currentStep === 1 && (
            <fieldset className="form-section">
                <legend className="form-section-title">Detalhes da Venda</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label htmlFor="clienteId">Cliente *</label>
                        <select id="clienteId" name="clienteId" value={formData.clienteId} onChange={onChange} required className="form-input" disabled={loadingClientes}>
                            <option value="">-- Selecione um Cliente --</option>
                            {clientes.filter(c => c?._id || c?.id).map(c => (
                                <option key={c._id || c.id} value={c._id || c.id}>{c.nomeCompleto || c.user?.name || 'Cliente sem nome'}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="ofertaId">Oferta (Pacote) *</label>
                        <select id="ofertaId" name="ofertaId" value={formData.ofertaId} onChange={onChange} required className="form-input" disabled={loadingOfertas}>
                            <option value="">-- Selecione uma Oferta --</option>
                            {ofertasVendaveis.filter(o => o?._id || o?.id).map(o => (
                                <option key={o._id || o.id} value={o._id || o.id}>{o.nome}</option>
                            ))}
                        </select>
                        <p className="form-hint">
                            {loadingOfertas ? 'Carregando ofertas...' : `${ofertasVendaveis.length} oferta(s) vendável(is) como pacote de sessões`}
                        </p>
                    </div>

                    {ofertaSelecionada?.tipoDePrecificacao === 'Por Pacote' && (
                        <div className="form-group">
                            <label htmlFor="pacoteDePrecoId">Pacote de Preço *</label>
                            <select id="pacoteDePrecoId" name="pacoteDePrecoId" value={formData.pacoteDePrecoId} onChange={onChange} required className="form-input">
                                <option value="">-- Selecione um Pacote --</option>
                                {(ofertaSelecionada.pacotesDePreco || []).filter(p => p?._id).map(p => (
                                    <option key={p._id} value={p._id}>{p.nome} ({p.sessoes} sessões) - {formatCurrency(p.valor)}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Valor</label>
                        <p className="form-static-text">
                            {ofertaSelecionada?.tipoDePrecificacao === 'Por Pacote'
                                ? formatCurrency(pacoteDePrecoSelecionado?.valor)
                                : formatCurrency(ofertaSelecionada?.valorFixo)}
                        </p>
                    </div>
                    <div className="form-group">
                        <label>Sessões</label>
                        <p className="form-static-text">
                            {ofertaSelecionada?.tipoDePrecificacao === 'Por Pacote'
                                ? (pacoteDePrecoSelecionado?.sessoes ?? '—')
                                : (ofertaSelecionada ? 1 : '—')}
                        </p>
                    </div>
                </div>
            </fieldset>
            )}

            {currentStep === 2 && (
            <fieldset className="form-section">
                <legend className="form-section-title">Pagamento (opcional)</legend>
                <p className="form-hint">Se uma unidade for selecionada, uma venda será registrada automaticamente para este pacote.</p>
                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label htmlFor="unidadeId">Unidade</label>
                        <select id="unidadeId" name="unidadeId" value={formData.unidadeId} onChange={onChange} className="form-input" disabled={loadingUnidades}>
                            <option value="">Não gerar venda agora</option>
                            {unidades.filter(u => u?._id || u?.id).map(u => (
                                <option key={u._id || u.id} value={u._id || u.id}>{u.nome}{u.localidade ? ` - ${u.localidade}` : ''}</option>
                            ))}
                        </select>
                    </div>
                    {formData.unidadeId && (
                        <div className="form-group">
                            <label htmlFor="metodoPagamentoId">Método de Pagamento</label>
                            <select id="metodoPagamentoId" name="metodoPagamentoId" value={formData.metodoPagamentoId} onChange={onChange} className="form-input">
                                <option value="">Pagamento pendente</option>
                                {metodos.filter(m => m?._id || m?.id).map(m => (
                                    <option key={m._id || m.id} value={m._id || m.id}>{m.nome}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </fieldset>
            )}

            {currentStep === 3 && (
            <fieldset className="form-section">
                <legend className="form-section-title">Observações</legend>
                <div className="form-group">
                    <textarea
                        name="observacoes"
                        value={formData.observacoes}
                        onChange={onChange}
                        rows="3"
                        className="form-input"
                        placeholder="Observações sobre esta venda (opcional)"
                    />
                </div>
            </fieldset>
            )}

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary" disabled={loading}>
                    <XCircle size={18} className="icon" />Cancelar
                </button>
                <FormStepNavigation
                    currentStep={currentStep}
                    totalSteps={STEP_LABELS.length}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={() => setCurrentStep((s) => s + 1)}
                    isSubmitting={loading}
                    submitLabel="Vender Pacote"
                />
            </div>
        </form>
    );
};

export default PacoteClienteForm;
